<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <!-- Header -->
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-calendar-clock me-2"></i>{{ $t('page.appointments', 'Appointments') }}</h4>
        </div>
        <div class="d-flex gap-2">
          <button class="hms-btn hms-btn-primary" @click="openBook">
            <i class="mdi mdi-calendar-plus me-1"></i>Book Appointment
          </button>
        </div>
      </div>

      <!-- Stats -->
      <div class="hms-stat-row">
        <div class="hms-stat" @click="setView('today')" :class="{ selected: view==='today' }">
          <div class="hms-stat-num hms-stat-info">{{ todayCount }}</div>
          <div class="hms-stat-lbl">Today</div>
        </div>
        <div class="hms-stat" @click="setView('upcoming')" :class="{ selected: view==='upcoming' }">
          <div class="hms-stat-num hms-stat-amber">{{ upcomingCount }}</div>
          <div class="hms-stat-lbl">Upcoming</div>
        </div>
        <div class="hms-stat" @click="setView('past')" :class="{ selected: view==='past' }">
          <div class="hms-stat-num">{{ pastCount }}</div>
          <div class="hms-stat-lbl">Past</div>
        </div>
        <div class="hms-stat" @click="setView('all')" :class="{ selected: view==='all' }">
          <div class="hms-stat-num">{{ appointments.length }}</div>
          <div class="hms-stat-lbl">All</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-green">{{ countByStatus('completed') }}</div>
          <div class="hms-stat-lbl">Completed</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-red">{{ countByStatus('no_show') + countByStatus('cancelled') }}</div>
          <div class="hms-stat-lbl">No-show / Cancelled</div>
        </div>
      </div>

      <div v-if="loading" class="hms-spinner"><div class="spinner-border"></div></div>

      <div v-else>
        <div class="hms-card">
          <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
            <div class="hms-search-wrap">
              <i class="mdi mdi-magnify"></i>
              <input class="hms-search" v-model="search" placeholder="Search patient, appointment number, doctor..." />
            </div>
            <select class="hms-input hms-filter" v-model="statusFilter">
              <option value="">All status</option>
              <option value="scheduled">Scheduled</option>
              <option value="confirmed">Confirmed</option>
              <option value="arrived">Arrived</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="no_show">No-show</option>
            </select>
            <select class="hms-input hms-filter" v-model="doctorFilter">
              <option value="">All doctors</option>
              <option v-for="d in doctors" :key="d.user_id" :value="d.user_id">{{ d.first_name }} {{ d.last_name }}</option>
            </select>
            <span class="hms-muted ms-auto">{{ filtered.length }} appointments</span>
          </div>

          <div v-if="filtered.length" style="overflow-x:auto;">
            <table class="hms-table">
              <thead><tr>
                <th>Appt #</th>
                <th>Patient</th>
                <th>Doctor / Dept</th>
                <th>Date</th>
                <th>Time</th>
                <th>Type</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Actions</th>
              </tr></thead>
              <tbody>
                <tr v-for="a in filtered" :key="a.appointment_id" :class="rowClass(a)">
                  <td><span class="hms-mono">{{ a.appointment_number }}</span></td>
                  <td>
                    <div class="hms-text fw-semibold">{{ a.patient_name }}</div>
                    <div class="hms-muted-sm">{{ a.patient_no }} · {{ a.phone || '' }}</div>
                  </td>
                  <td>
                    <div>{{ a.doctor_name || '—' }}</div>
                    <div class="hms-muted-sm">{{ a.department || '' }}</div>
                  </td>
                  <td>{{ formatDate(a.appointment_date) }}</td>
                  <td>{{ a.appointment_time || '—' }}</td>
                  <td><span class="hms-badge hms-badge-info">{{ formatType(a.appointment_type) }}</span></td>
                  <td><span class="hms-text-clip">{{ a.reason || '—' }}</span></td>
                  <td><span class="hms-badge" :class="statusBadge(a.status)">{{ formatStatus(a.status) }}</span></td>
                  <td>
                    <button v-if="canComplete(a)" class="hms-btn hms-btn-sm hms-btn-success me-1" @click="complete(a)" title="Mark completed">
                      <i class="mdi mdi-check"></i>
                    </button>
                    <button v-if="canCancel(a)" class="hms-btn hms-btn-sm hms-btn-red" @click="openCancel(a)" title="Cancel">
                      <i class="mdi mdi-close"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="hms-empty"><i class="mdi mdi-calendar-blank-outline"></i>No appointments match.</div>
        </div>
      </div>

      <!-- ============ BOOK MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showBook" @click.self="showBook=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-calendar-plus me-2"></i>Book Appointment</span>
            <button class="hms-modal-close" @click="showBook=false">&times;</button>
          </div>
          <div v-if="bookError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ bookError }}</div>

          <div class="mb-3">
            <label class="hms-label">Patient *</label>
            <div class="hms-search-wrap">
              <i class="mdi mdi-magnify"></i>
              <input class="hms-search" v-model="patientSearch" placeholder="Search patient..." @input="onPatientSearch" />
            </div>
            <div v-if="patientResults.length" class="hms-suggest">
              <div v-for="pp in patientResults" :key="pp.patient_id" class="hms-suggest-item" @click="pickPatient(pp)">
                <div class="hms-text fw-semibold">{{ pp.first_name }} {{ pp.last_name }}</div>
                <div class="hms-muted-sm">{{ pp.patient_no }} · {{ pp.phone || '—' }}</div>
              </div>
            </div>
            <div v-if="bookForm.patient_id" class="hms-picked mt-2">
              <i class="mdi mdi-check-circle me-1"></i>{{ bookForm.patient_name }} ({{ bookForm.patient_no }})
              <button class="hms-modal-close ms-auto" @click="clearPatient">&times;</button>
            </div>
          </div>

          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">Doctor</label>
              <select class="hms-input" v-model="bookForm.doctor_id">
                <option value="">Any available</option>
                <option v-for="d in doctors" :key="d.user_id" :value="d.user_id">{{ d.first_name }} {{ d.last_name }}</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Department</label>
              <select class="hms-input" v-model="bookForm.department">
                <option value="">— Select department —</option>
                <option v-for="d in departmentOptions" :key="d" :value="d">{{ d }}</option>
              </select>
            </div>
            <div class="col-md-4"><label class="hms-label">Date *</label>
              <input type="date" class="hms-input" v-model="bookForm.appointment_date" :min="todayIso" />
            </div>
            <div class="col-md-4"><label class="hms-label">Time</label>
              <input type="time" class="hms-input" v-model="bookForm.appointment_time" />
            </div>
            <div class="col-md-4"><label class="hms-label">Duration (min)</label>
              <input type="number" class="hms-input" v-model.number="bookForm.duration_minutes" min="10" step="5" />
            </div>
            <div class="col-md-12"><label class="hms-label">Type</label>
              <select class="hms-input" v-model="bookForm.appointment_type">
                <option value="new">New</option>
                <option value="follow_up">Follow-up</option>
                <option value="specialist">Specialist</option>
                <option value="procedure">Procedure</option>
                <option value="telemedicine">Telemedicine</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Reason</label>
              <textarea class="hms-input" rows="2" v-model="bookForm.reason"></textarea>
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="doBook" :disabled="processing || !bookForm.patient_id || !bookForm.appointment_date">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>{{ processing ? 'Booking...' : 'Book Appointment' }}
          </button>
        </div>
      </div>

      <!-- Cancel modal -->
      <div class="hms-modal-overlay" v-if="cancelTarget" @click.self="cancelTarget=null">
        <div class="hms-modal" style="max-width:480px;">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-close-circle me-2 text-danger"></i>Cancel Appointment</span>
            <button class="hms-modal-close" @click="cancelTarget=null">&times;</button>
          </div>
          <p class="hms-muted">{{ cancelTarget.appointment_number }} · {{ cancelTarget.patient_name }} · {{ formatDate(cancelTarget.appointment_date) }}</p>
          <div class="mb-3"><label class="hms-label">Reason</label>
            <textarea class="hms-input" rows="3" v-model="cancelReason"></textarea>
          </div>
          <div class="d-flex gap-2">
            <button class="hms-btn hms-btn-blue flex-fill" @click="cancelTarget=null">Don't cancel</button>
            <button class="hms-btn hms-btn-red flex-fill" @click="doCancel" :disabled="processing">
              <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>Confirm Cancel
            </button>
          </div>
        </div>
      </div>

      <!-- Toast -->
      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root,
html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;--hms-placeholder:#4a4a4a;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;--hms-placeholder:#aaaaaa;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }
.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-text { color: var(--hms-h); }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.hms-text-clip { display: inline-block; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: middle; }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 140px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { border-color: var(--hms-primary); transform: translateY(-2px); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-primary); line-height: 1.1; }
.hms-stat-lbl { font-size: 12px; color: var(--hms-s); margin-top: 4px; }
.hms-stat-info { color: var(--hms-primary); }
.hms-stat-amber { color: #f1b44c; }
.hms-stat-green { color: var(--hms-success); }
.hms-stat-red { color: var(--hms-danger); }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 20px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

.hms-search-wrap { position: relative; flex: 1; min-width: 240px; max-width: 360px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }
.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-filter { width: auto; padding: 7px 12px; font-size: 13px; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }

.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); vertical-align: middle; }
.hms-table tr:hover td { background: var(--hms-primary-lt); }
.hms-row-today td { background: rgba(40,167,69,.06); }

.hms-badge { padding: 3px 10px; border-radius: 50px; font-size: 11px; font-weight: 600; text-transform: capitalize; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: var(--hms-warning); }
.hms-badge-danger  { background: rgba(220,53,69,.15);  color: var(--hms-danger); }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }
.hms-badge-amber   { background: rgba(245,158,11,.15); color: #f59e0b; }
.hms-badge-purple  { background: rgba(155,89,182,.15); color: #9b59b6; }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-red    { background: rgba(220,53,69,.15);   color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-btn-red:hover:not(:disabled) { background: rgba(220,53,69,.25); }
.hms-btn-success { background: rgba(40,167,69,.15); color: var(--hms-success); border: 1px solid rgba(40,167,69,.3); }
.hms-btn-success:hover:not(:disabled) { background: var(--hms-success); color: #fff; }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 720px; max-height: calc(100vh - 80px); overflow-y: auto; box-shadow: 0 24px 60px rgba(0,0,0,.3); }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }
.hms-modal-close:hover { color: var(--hms-h); }

.hms-suggest { background: var(--hms-bg4); border: 1px solid var(--hms-br); border-radius: 8px; margin-top: 6px; max-height: 200px; overflow-y: auto; }
.hms-suggest-item { padding: 8px 12px; cursor: pointer; border-bottom: 1px solid var(--hms-br); }
.hms-suggest-item:last-child { border-bottom: none; }
.hms-suggest-item:hover { background: var(--hms-primary-lt); }
.hms-picked { background: rgba(40,167,69,.08); border: 1px solid rgba(40,167,69,.3); border-radius: 8px; padding: 8px 12px; color: var(--hms-success); font-size: 13px; display: flex; align-items: center; }
.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "AppointmentsManagement",
  components: { Navigation },
  data() {
    return {
      loading: true,
      processing: false,
      view: "today",
      search: "",
      statusFilter: "",
      doctorFilter: "",

      appointments: [],
      doctors: [],
      // Populated from queue_departments per facility (getQueueDepartments).
      // Falls back to a small default set if the facility hasn't configured any.
      departmentOptions: [],
      departmentFallback: [
        'OPD (General)', 'Casualty / Emergency', 'Paediatrics', 'Maternity / ANC',
        'Pharmacy', 'Laboratory', 'Radiology', 'Other'
      ],

      showBook: false,
      bookError: "",
      bookForm: this.emptyBook(),
      patientSearch: "",
      patientSearchTimer: null,
      patientResults: [],

      cancelTarget: null,
      cancelReason: "",

      toastMsg: "", toastError: false, toastTimer: null,
      branchId: sessionStorage.getItem("branchId")
    };
  },
  computed: {
    todayIso() { return new Date().toISOString().slice(0,10); },
    todayCount()    { return this.appointments.filter(a => (a.appointment_date || "").slice(0,10) === this.todayIso).length; },
    upcomingCount() { return this.appointments.filter(a => (a.appointment_date || "").slice(0,10) > this.todayIso).length; },
    pastCount()     { return this.appointments.filter(a => (a.appointment_date || "").slice(0,10) < this.todayIso).length; },
    filtered() {
      var list = this.appointments;
      if (this.view === 'today')    list = list.filter(a => (a.appointment_date || "").slice(0,10) === this.todayIso);
      if (this.view === 'upcoming') list = list.filter(a => (a.appointment_date || "").slice(0,10) > this.todayIso);
      if (this.view === 'past')     list = list.filter(a => (a.appointment_date || "").slice(0,10) < this.todayIso);
      if (this.statusFilter)        list = list.filter(a => a.status === this.statusFilter);
      if (this.doctorFilter)        list = list.filter(a => a.doctor_id == this.doctorFilter);
      var q = this.search.trim().toLowerCase();
      if (q) list = list.filter(a => ((a.patient_name||"")+" "+(a.appointment_number||"")+" "+(a.doctor_name||"")+" "+(a.department||"")).toLowerCase().indexOf(q) >= 0);
      return list;
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.applyTheme();
    this.loadAll();
  },
  beforeUnmount() {
    if (this.patientSearchTimer) clearTimeout(this.patientSearchTimer);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    emptyBook() {
      return { patient_id: null, patient_name: "", patient_no: "",
        doctor_id: "", department: "", appointment_type: "new",
        appointment_date: "", appointment_time: "", duration_minutes: 30, reason: "" };
    },
    countByStatus(s) { return this.appointments.filter(a => a.status === s).length; },
    setView(v) { this.view = v; },
    rowClass(a) { return (a.appointment_date || "").slice(0,10) === this.todayIso ? 'hms-row-today' : ''; },
    canComplete(a) { return ['scheduled','confirmed','arrived','in_progress'].indexOf(a.status) >= 0; },
    canCancel(a) { return ['scheduled','confirmed','arrived'].indexOf(a.status) >= 0; },

    formatDate(d) { if (!d) return "—"; try { var dt=new Date(d); if(isNaN(dt.getTime())) return d; return dt.toLocaleDateString(undefined,{day:'2-digit',month:'short',year:'numeric'}); } catch(e){return d;} },
    formatType(t) { return ({ new:'New', follow_up:'Follow-up', specialist:'Specialist', procedure:'Procedure', telemedicine:'Telemedicine' })[t] || t; },
    formatStatus(s) { return ({ scheduled:'Scheduled', confirmed:'Confirmed', arrived:'Arrived', in_progress:'In Progress', completed:'Completed', cancelled:'Cancelled', no_show:'No-show' })[s] || s; },
    statusBadge(s) {
      var m = { scheduled:'hms-badge-info', confirmed:'hms-badge-info', arrived:'hms-badge-amber', in_progress:'hms-badge-purple', completed:'hms-badge-success', cancelled:'hms-badge-danger', no_show:'hms-badge-danger' };
      return m[s] || 'hms-badge-neutral';
    },
    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => { self.toastMsg = ""; }, 2400);
    },

    async loadAll() {
      this.loading = true;
      try { await Promise.all([this.loadAppointments(), this.loadDoctors(), this.loadDepartments()]); }
      catch(e) { console.error(e); }
      this.loading = false;
    },
    async loadAppointments() {
      try {
        var r = await executeJson({ function: "getAppointments", branch_id: this.branchId }, hms);
        if (r.data && r.data.success) this.appointments = (r.data.data && r.data.data.Appointments) || [];
      } catch(e) {}
    },
    async loadDoctors() {
      try {
        var r = await executeJson({ function: "getDoctors", branch_id: this.branchId }, hms);
        if (r.data && r.data.success) this.doctors = (r.data.data && r.data.data.Doctors) || [];
      } catch(e) {}
    },
    async loadDepartments() {
      try {
        var r = await executeJson({ function: "getQueueDepartments", branch_id: this.branchId }, hms);
        var rows = (r.data && r.data.success && r.data.data && r.data.data.Departments) || [];
        var names = rows.map(function (d) { return d.dept_name; }).filter(Boolean);
        this.departmentOptions = names.length ? names : this.departmentFallback.slice();
      } catch(e) {
        this.departmentOptions = this.departmentFallback.slice();
      }
    },

    openBook() {
      this.bookForm = this.emptyBook();
      this.bookForm.appointment_date = this.todayIso;
      this.bookError = "";
      this.patientSearch = "";
      this.patientResults = [];
      this.showBook = true;
    },
    onPatientSearch() {
      if (this.patientSearchTimer) clearTimeout(this.patientSearchTimer);
      var self = this;
      if (!this.patientSearch || this.patientSearch.length < 2) { this.patientResults = []; return; }
      this.patientSearchTimer = setTimeout(async () => {
        try {
          var r = await executeJson({ function: "searchPatients", q: self.patientSearch.trim() }, hms);
          if (r.data && r.data.success) self.patientResults = (r.data.data && r.data.data.Patients) || [];
        } catch(e) {}
      }, 300);
    },
    pickPatient(p) {
      this.bookForm.patient_id = p.patient_id;
      this.bookForm.patient_name = (p.first_name + ' ' + p.last_name).trim();
      this.bookForm.patient_no = p.patient_no;
      this.patientResults = []; this.patientSearch = "";
    },
    clearPatient() { this.bookForm.patient_id = null; this.bookForm.patient_name = ""; this.bookForm.patient_no = ""; },

    async doBook() {
      this.bookError = "";
      if (!this.bookForm.patient_id) { this.bookError = "Pick a patient."; return; }
      if (!this.bookForm.appointment_date) { this.bookError = "Date is required."; return; }
      var payload = Object.assign({ function: "bookAppointment" }, this.bookForm);
      delete payload.patient_name; delete payload.patient_no;

      this.processing = true;
      try {
        var r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          this.showToast("Appointment booked: " + r.data.data.appointment_number);
          this.showBook = false;
          await this.loadAppointments();
        } else { this.bookError = r.data.message || "Failed."; }
      } catch(e) { this.bookError = "Connection error."; }
      this.processing = false;
    },

    async complete(a) {
      this.processing = true;
      try {
        var r = await executeJson({ function: "completeAppointment", appointment_id: a.appointment_id }, hms);
        if (r.data && r.data.success) { this.showToast("Marked complete"); await this.loadAppointments(); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },
    openCancel(a) { this.cancelTarget = a; this.cancelReason = ""; },
    async doCancel() {
      this.processing = true;
      try {
        var r = await executeJson({ function: "cancelAppointment", appointment_id: this.cancelTarget.appointment_id, reason: this.cancelReason }, hms);
        if (r.data && r.data.success) {
          this.showToast("Cancelled");
          this.cancelTarget = null;
          await this.loadAppointments();
        }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    }
  }
};
</script>
